import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox } from '@xsolla/ui-core';
import { AlertDangerColorful } from '@xsolla/ui-icons';
import { Modal } from '@xsolla/ui-modal';

import { deleteUser } from '../../../../api';
import { FormFooter } from '../../../ui';
import { CheckboxWrapper, DeleteFormContent, IconWrapper, TitleHeader } from './style';

interface Props {
  userId: string;
  projectId: string;
  onClose: () => void;
  onDelete: () => unknown;
}

const DeleteUserModal = ({ onClose, onDelete, userId, projectId }: Props): JSX.Element => {
  const intl = useIntl();
  const [valueCheck, setValueCheck] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);

  const handleChangeCheck = useCallback(() => {
    setValueCheck(!valueCheck);
  }, [valueCheck]);

  const handleOnDelete = useCallback(async () => {
    if (valueCheck && userId) {
      setError(false);
      setFetching(true);
      try {
        await deleteUser(userId, projectId);
        onDelete();
      } catch (err) {
        setError(true);
      } finally {
        setFetching(false);
      }
    }
  }, [valueCheck]);

  return (
    <Modal>
      <DeleteFormContent>
        <IconWrapper>
          <AlertDangerColorful size={40} />
        </IconWrapper>

        <TitleHeader indent="sm" data-test-id="user-delete-modal-header">
          {intl.formatMessage({ id: 'users.settings.delete-user.alert.title' })}
        </TitleHeader>

        <p>{intl.formatMessage({ id: 'users.settings.delete-user.alert.subtitle' })}</p>
      </DeleteFormContent>

      <CheckboxWrapper>
        <Checkbox checked={valueCheck} disabled={fetching} onChange={handleChangeCheck}>
          {intl.formatMessage({ id: 'users.settings.delete-user.alert.checked-understand' })}
        </Checkbox>
      </CheckboxWrapper>

      <FormFooter
        onSubmit={handleOnDelete}
        onCancel={onClose}
        fetching={fetching}
        error={error}
        isModal
        disabled={!valueCheck}
        submitButtonPalette="danger"
        submitText={intl.formatMessage({ id: 'button.yes-delete' })}
        data-test-id="user-delete-modal-footer"
      />
    </Modal>
  );
};

export default DeleteUserModal;
